import * as React from "react";
import { IACL, IACLEmail } from "intake_shared/src/types";
import { canAutoAssignToCoordinator } from "intake_shared/src/assignment";
import { SketchPicker } from "react-color";
import { useToasts } from "react-toast-notifications";
import store from "../../../../store";
import { AclContext } from "../../../../contexts/AclContext";
import { hasOfficeAdminBetaAccess } from "../../../../appSettings";
import { PageHeader } from "../../bits/PageHeader/PageHeader";
import { Button } from "../../bits/Button/Button";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Input } from "../../bits/Input/Input";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { FormCheckbox } from "../../bits/FormCheckbox/FormCheckbox";
import { Badge } from "../../bits/Badge/Badge";
import Alert from "../../bits/Alert/Alert";
import { Card } from "../../bits/Card/Card";
import Table from "../../bits/Table/Table";
import TrashIcon from "../../icons/Trash";
import styles from "./UserManagement.module.scss";

const defaultAcl: IACL = {
    billing: false,
    insurance_verification: false,
    intake: false,
    care_coordinator: false,
    super_admin: false,
    blocked: false,
    view_applicable_fees: false,
    office_manager: false,
    can_be_auto_assigned: false,
    copay: false,
};
const permissions: { key: keyof IACL; label: string }[] = [
    { key: "super_admin", label: "Super Admin" },
    { key: "billing", label: "Billing" },
    { key: "intake", label: "Intake" },
    { key: "care_coordinator", label: "Care Coordinator" },
    { key: "copay", label: "Copay" },
    { key: "view_applicable_fees", label: "View Applicable Fees" },
    { key: "office_manager", label: "Office Manager" },
];
function enabled(acl: IACL | undefined, key: keyof IACL) {
    return key === "billing"
        ? Boolean(acl?.billing || acl?.insurance_verification)
        : Boolean(acl?.[key]);
}

export default function UserManagement() {
    const { addToast } = useToasts();
    const authUserEmail = React.useContext(AclContext)?.authUser.user?.email || "";
    const [allAcls, setAllAcls] = React.useState<IACLEmail[] | null>(null);
    const [search, setSearch] = React.useState("");
    const searchTerm = search.trim().toLowerCase();
    const filteredAcls =
        allAcls?.filter(
            (user) =>
                user.email.toLowerCase().includes(searchTerm) ||
                permissions.some(
                    (permission) =>
                        enabled(user.acl, permission.key) &&
                        permission.label.toLowerCase().includes(searchTerm)
                )
        ) || [];
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState("");
    const [pageSuccess, setPageSuccess] = React.useState("");
    const [showEditor, setShowEditor] = React.useState(false);
    const [selected, setSelected] = React.useState<IACLEmail | null>(null);
    const [userEmail, setUserEmail] = React.useState("");
    const [userAcl, setUserAcl] = React.useState<IACL>({ ...defaultAcl });
    const [userColor, setUserColor] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState("");
    const [saved, setSaved] = React.useState(false);
    const [showDiscard, setShowDiscard] = React.useState(false);
    const [showColor, setShowColor] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");
    const savePending = React.useRef(false);
    const deletePending = React.useRef(false);
    const loadPending = React.useRef(false);
    const addButton = React.useRef<HTMLButtonElement>(null);
    const colorButton = React.useRef<HTMLButtonElement>(null);
    const colorPicker = React.useRef<HTMLDivElement>(null);
    const discardPrompt = React.useRef<HTMLDivElement>(null);
    const tableRoot = React.useRef<HTMLDivElement>(null);
    const openingEmail = React.useRef("");
    const emailInput = React.useRef<HTMLInputElement>(null);
    const saveErrorContainer = React.useRef<HTMLDivElement>(null);
    const deleteDescription = React.useRef<HTMLDivElement>(null);
    React.useLayoutEffect(() => {
        if (deleting) {
            deleteDescription.current
                ?.closest<HTMLElement>('[role="alertdialog"]')
                ?.focus();
        }
    }, [deleting]);
    const reportSaveError = (message: string) => {
        setSaveError(message);
        requestAnimationFrame(() => {
            const error = saveErrorContainer.current;
            error?.focus({ preventScroll: true });
            error?.scrollIntoView?.({ block: "nearest" });
        });
    };

    const getAllACLs = React.useCallback(async () => {
        if (loadPending.current) return;
        loadPending.current = true;
        setLoading(true);
        setLoadError("");
        try {
            const acls = await store.getAllACL();
            if (!acls) throw new Error("User list is unavailable");
            setAllAcls(acls);
        } catch {
            setLoadError("Could not load users. Try again.");
        } finally {
            loadPending.current = false;
            setLoading(false);
        }
    }, []);
    React.useEffect(() => {
        void getAllACLs();
    }, [getAllACLs]);
    React.useEffect(() => {
        setSaved(false);
        setSaveError("");
    }, [userAcl, userColor, userEmail]);
    const originalAcl = { ...defaultAcl, ...selected?.acl };
    const dirty =
        userEmail !== (selected?.email || "") ||
        userColor !== (selected ? selected.assignment_color || "#ffffff" : "") ||
        Object.keys(defaultAcl).some(
            (key) =>
                Boolean(userAcl[key as keyof IACL]) !==
                Boolean(originalAcl[key as keyof IACL])
        );
    const pending = saving || deleting;
    const openEditor = (user: IACLEmail | null) => {
        openingEmail.current = user?.email || "";
        setSelected(user);
        setUserEmail(user?.email || "");
        setUserAcl(user?.acl ? { ...user.acl } : { ...defaultAcl });
        setUserColor(user ? user.assignment_color || "#ffffff" : "");
        setSaved(false);
        setSaveError("");
        setShowDiscard(false);
        setShowColor(false);
        setShowEditor(true);
    };
    const restoreFocus = () =>
        requestAnimationFrame(() => {
            const manage = Array.from(
                tableRoot.current?.querySelectorAll<HTMLButtonElement>(
                    "button[data-user-email]"
                ) || []
            ).find((button) => button.dataset.userEmail === openingEmail.current);
            (manage || addButton.current)?.focus({ preventScroll: true });
        });
    const closeColor = () => {
        setShowColor(false);
        requestAnimationFrame(() => colorButton.current?.focus());
    };
    const closeEditor = (discard = false) => {
        if (savePending.current || deletePending.current || showDelete) return;
        if (showColor) {
            closeColor();
            return;
        }
        if (dirty && !discard) {
            setShowDiscard(true);
            requestAnimationFrame(() => discardPrompt.current?.focus());
            return;
        }
        setShowEditor(false);
        setShowDiscard(false);
        restoreFocus();
    };
    const saveUser = async () => {
        if (savePending.current || deletePending.current) return;
        if (!selected && !emailInput.current?.reportValidity()) return;
        if (
            selected &&
            selected.acl?.care_coordinator !== true &&
            userAcl.care_coordinator === true &&
            userEmail !== userEmail.trim().toLowerCase()
        ) {
            setSaved(false);
            reportSaveError(
                "This account's email needs an identity review before granting Care Coordinator. No changes were saved."
            );
            return;
        }
        savePending.current = true;
        setSaving(true);
        setSaveError("");
        setSaved(false);
        setPageSuccess("");
        const updated = {
            email: selected ? userEmail : userEmail.trim().toLowerCase(),
            acl: userAcl,
            assignment_color: userColor,
        };
        try {
            await store.saveAcl(updated);
            setShowDiscard(false);
            if (selected) {
                setAllAcls(
                    (previous) =>
                        previous?.map((user) =>
                            user.email === updated.email
                                ? { ...user, ...updated }
                                : user
                        ) || null
                );
                setSelected({ ...selected, ...updated });
                setSaved(true);
                addToast("User details updated successfully", {
                    appearance: "success",
                });
            } else {
                // Adding does not reactivate soft-deleted ACLs; show the server's active roster.
                await getAllACLs();
                setShowEditor(false);
                setPageSuccess("User added successfully.");
                restoreFocus();
                addToast("User added successfully", { appearance: "success" });
            }
        } catch {
            reportSaveError(
                selected
                    ? "Could not save user details. Try again."
                    : "Could not add user. Try again."
            );
            addToast(
                selected ? "Failed to update user details" : "Failed to add user",
                { appearance: "error" }
            );
        } finally {
            savePending.current = false;
            setSaving(false);
        }
    };
    const closeDelete = () => {
        if (!deletePending.current) {
            setShowDelete(false);
            setDeleteError("");
        }
    };
    const deleteUser = async () => {
        if (!selected || deletePending.current || savePending.current) return;
        deletePending.current = true;
        setDeleting(true);
        setDeleteError("");
        setPageSuccess("");
        try {
            await store.saveAcl({
                email: selected.email,
                deleted: true,
                deleted_at: new Date().toISOString(),
                deleted_by: authUserEmail,
                acl: { ...selected.acl },
            });
            setAllAcls(
                (previous) =>
                    previous?.filter((user) => user.email !== selected.email) || null
            );
            setShowDelete(false);
            setShowEditor(false);
            setPageSuccess("User deleted successfully.");
            addToast("User deleted successfully", { appearance: "success" });
            restoreFocus();
        } catch {
            setDeleteError("Could not delete user. Try again.");
            addToast("Failed to delete user", { appearance: "error" });
        } finally {
            deletePending.current = false;
            setDeleting(false);
        }
    };
    const columns = [
        {
            Header: "User",
            accessor: "email",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: IACLEmail } }) => (
                <div className={styles.userCell}>
                    <span
                        className={styles.colorDot}
                        aria-hidden="true"
                        style={{
                            backgroundColor:
                                row.original.assignment_color || "#ffffff",
                        }}
                    />
                    <span>{row.original.email}</span>
                </div>
            ),
        },
        {
            Header: "Status",
            id: "status",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: IACLEmail } }) => (
                <Badge
                    tone={row.original.acl?.blocked ? "danger" : "success"}
                    content={row.original.acl?.blocked ? "Blocked" : "Active"}
                />
            ),
        },
        {
            Header: "Auto-assign",
            id: "autoassign",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: IACLEmail } }) => (
                <span>
                    {row.original.acl?.can_be_auto_assigned !== true
                        ? "No"
                        : row.original.acl?.care_coordinator !== true
                        ? "Needs coordinator role"
                        : canAutoAssignToCoordinator(row.original)
                        ? "Yes"
                        : "No (inactive account)"}
                </span>
            ),
        },
        {
            Header: "Roles",
            id: "roles",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: IACLEmail } }) => {
                const active = permissions.filter((permission) =>
                    enabled(row.original.acl, permission.key)
                );
                return (
                    <div className={styles.roles}>
                        {active.length
                            ? active.map((permission) => (
                                  <Badge
                                      key={permission.key}
                                      content={permission.label}
                                      className={styles.roleBadge}
                                  />
                              ))
                            : "None"}
                    </div>
                );
            },
        },
        {
            Header: "Actions",
            id: "manage",
            disableSortBy: true,
            Cell: ({ row }: { row: { original: IACLEmail } }) => (
                <Button
                    variant="secondary"
                    data-user-email={row.original.email}
                    aria-label={`Manage ${row.original.email}`}
                    disabled={pending || loading}
                    onClick={() => openEditor(row.original)}
                >
                    Manage
                </Button>
            ),
        },
    ];
    const editDisabled = pending || Boolean(selected && userAcl.blocked);

    return (
        <div className={`v2-page ${styles.userManagement}`}>
            <PageHeader
                title="User Management"
                meta={allAcls ? `${allAcls.length} users` : undefined}
                actions={
                    <Button
                        ref={addButton}
                        onClick={() => openEditor(null)}
                        disabled={loading || !allAcls || pending}
                    >
                        Add new user
                    </Button>
                }
            />
            {loadError && (
                <Alert type="danger" contentClassName={styles.feedbackRow}>
                    <span>{loadError}</span>
                    <Button
                        variant="secondary"
                        disabled={loading}
                        onClick={getAllACLs}
                    >
                        Try again
                    </Button>
                </Alert>
            )}
            {pageSuccess && <Alert type="success">{pageSuccess}</Alert>}
            {loading && !allAcls && (
                <Card>
                    <p role="status">Loading users…</p>
                </Card>
            )}
            {allAcls && allAcls.length > 0 && (
                <div className={styles.searchToolbar}>
                    <div className={styles.searchField}>
                        <label htmlFor="user-management-search" className="sr-only">
                            Search users
                        </label>
                        <SearchInput
                            id="user-management-search"
                            name="user-management-search"
                            placeholder="Search users by email or role"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </div>
                    {search && (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setSearch("");
                                document
                                    .getElementById("user-management-search")
                                    ?.focus();
                            }}
                        >
                            Clear search
                        </Button>
                    )}
                    {searchTerm && (
                        <span className={styles.helper} role="status">
                            {filteredAcls.length} of {allAcls.length} users
                        </span>
                    )}
                </div>
            )}
            {allAcls && allAcls.length === 0 && (
                <Card>
                    <p className={styles.helper}>No users found.</p>
                </Card>
            )}
            {allAcls && allAcls.length > 0 && filteredAcls.length === 0 && (
                <Card>
                    <p className={styles.helper}>No users match your search.</p>
                </Card>
            )}
            {filteredAcls.length > 0 && (
                <div ref={tableRoot}>
                    <Table
                        columns={columns}
                        data={filteredAcls}
                        allowSelect={false}
                        stickyLastColumn
                    />
                </div>
            )}
            <Dialog
                open={showEditor}
                title={selected ? "Manage user" : "Add new user"}
                ariaLabel={
                    selected ? `Manage user ${selected.email}` : "Add new user"
                }
                onClose={() => closeEditor()}
                showFooter={false}
                wrapperClassName={styles.editorDialog}
                focusManagement={{ paused: showDelete }}
            >
                <div className={styles.editorContent}>
                    {showDiscard && (
                        <div
                            ref={discardPrompt}
                            tabIndex={-1}
                            role="group"
                            aria-label="Unsaved user changes"
                            className={styles.discardPrompt}
                        >
                            <p>Your user changes have not been saved.</p>
                            <div className={styles.actionGroup}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowDiscard(false);
                                        emailInput.current?.focus();
                                    }}
                                >
                                    Keep editing
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => closeEditor(true)}
                                >
                                    Discard changes
                                </Button>
                            </div>
                        </div>
                    )}
                    {saveError && (
                        <div
                            ref={saveErrorContainer}
                            tabIndex={-1}
                            role="group"
                            aria-label="User details error"
                        >
                            <Alert type="danger">{saveError}</Alert>
                        </div>
                    )}
                    {saved && <Alert type="success">User details saved.</Alert>}
                    <fieldset disabled={pending} className={styles.editorFields}>
                        <Input
                            ref={emailInput}
                            id="user-management-email"
                            name="user-management-email"
                            label={
                                selected
                                    ? "Email"
                                    : "Email (Only @humantold.com email addresses are accepted)"
                            }
                            isRequired={!selected}
                            required={!selected}
                            type="email"
                            value={userEmail}
                            readOnly={Boolean(selected)}
                            placeholder="Email Address"
                            onChange={(event) => setUserEmail(event.target.value)}
                        />
                        <section className={styles.section} aria-label="Permissions">
                            <h2>Permissions</h2>
                            <div className={styles.permissionsGrid}>
                                {permissions
                                    .filter(
                                        (permission) =>
                                            permission.key !== "office_manager" ||
                                            hasOfficeAdminBetaAccess(authUserEmail)
                                    )
                                    .map((permission) => (
                                        <FormCheckbox
                                            key={permission.key}
                                            id={`user-management-${permission.key}`}
                                            label={permission.label}
                                            checked={enabled(
                                                userAcl,
                                                permission.key
                                            )}
                                            disabled={editDisabled}
                                            onChange={() =>
                                                setUserAcl({
                                                    ...userAcl,
                                                    [permission.key]:
                                                        !userAcl[permission.key],
                                                })
                                            }
                                        />
                                    ))}
                            </div>
                        </section>
                        <section
                            className={styles.section}
                            aria-label="Special settings"
                        >
                            <h2>Special settings</h2>
                            <div className={styles.permissionsGrid}>
                                <FormCheckbox
                                    id="user-management-blocked"
                                    label="Blocked"
                                    checked={Boolean(userAcl.blocked)}
                                    onChange={() =>
                                        setUserAcl({
                                            ...userAcl,
                                            blocked: !userAcl.blocked,
                                        })
                                    }
                                />
                                <FormCheckbox
                                    id="user-management-autoassign"
                                    label="Can be auto assigned"
                                    description="Requires the Care Coordinator role. Pausing keeps the role and existing clients."
                                    checked={Boolean(userAcl.can_be_auto_assigned)}
                                    disabled={editDisabled}
                                    onChange={() =>
                                        setUserAcl({
                                            ...userAcl,
                                            can_be_auto_assigned:
                                                !userAcl.can_be_auto_assigned,
                                        })
                                    }
                                />
                            </div>
                        </section>
                        <section className={styles.section} aria-label="User color">
                            <h2>User color</h2>
                            <div className={styles.colorControl}>
                                <span
                                    className={styles.colorPreview}
                                    aria-hidden="true"
                                    style={{
                                        backgroundColor: userColor || "#ffffff",
                                    }}
                                />
                                <Button
                                    ref={colorButton}
                                    variant="secondary"
                                    disabled={editDisabled}
                                    aria-expanded={showColor}
                                    aria-controls="user-management-color-picker"
                                    onClick={() => {
                                        setShowColor(!showColor);
                                        if (!showColor)
                                            requestAnimationFrame(() =>
                                                colorPicker.current
                                                    ?.querySelector<HTMLInputElement>(
                                                        "input"
                                                    )
                                                    ?.focus()
                                            );
                                    }}
                                >
                                    Change user color
                                </Button>
                            </div>
                            {showColor && (
                                <div
                                    ref={colorPicker}
                                    id="user-management-color-picker"
                                    role="group"
                                    aria-label="Choose user color"
                                    className={styles.colorPicker}
                                >
                                    <SketchPicker
                                        color={userColor || "#ffffff"}
                                        onChange={(color) => {
                                            setUserColor(color.hex);
                                        }}
                                    />
                                    <Button variant="secondary" onClick={closeColor}>
                                        Done
                                    </Button>
                                </div>
                            )}
                        </section>
                    </fieldset>
                    <div className={styles.editorActions}>
                        {selected && (
                            <Button
                                variant="danger"
                                disabled={pending}
                                iconLeft={<TrashIcon stroke="var(--color-white)" />}
                                onClick={() => {
                                    setDeleteError("");
                                    setShowColor(false);
                                    setShowDelete(true);
                                }}
                            >
                                Delete user
                            </Button>
                        )}
                        <div className={styles.saveActions}>
                            <Button
                                variant="secondary"
                                disabled={pending}
                                onClick={() => closeEditor()}
                            >
                                Cancel
                            </Button>
                            <Button disabled={pending} onClick={saveUser}>
                                {saving
                                    ? selected
                                        ? "Saving…"
                                        : "Adding…"
                                    : selected
                                    ? "Save changes"
                                    : "Add User"}
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
            <AlertDialog
                open={showDelete}
                onClose={closeDelete}
                title="Delete user"
                saveText={deleting ? "Deleting…" : "Delete"}
                isDeleting
                isSaveBtnDisabled={deleting}
                isCancelBtnDisabled={deleting}
                description={
                    <div
                        className={styles.deleteDescription}
                        ref={deleteDescription}
                    >
                        {deleteError && <Alert type="danger">{deleteError}</Alert>}
                        <p>
                            Are you sure you want to remove this user with email{" "}
                            <b>{selected?.email}</b>? This action cannot be reversed.
                        </p>
                    </div>
                }
                onSaveClick={deleteUser}
            />
        </div>
    );
}
